// Branded landing-page hero. Inline SVG illustration auto-tints to the
// facility's primary/accent colors via CSS custom properties (--brand-primary,
// --brand-accent set by branding.jsx). No external assets, fully self-hosted,
// HIPAA-clean. Each facility can override via hero_tagline / hero_subtext.

export default function Hero({
  tagline,
  subtext,
  facilityName,
  primary  = '#1d4ed8',
  accent   = '#0ea5e9',
  ctaLabel = 'Sign in',
  onCta    = () => {},
  secondaryLabel,
  onSecondary,
}) {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(135deg, ${primary}0d 0%, ${accent}1a 100%)`,
      }}
    >
      <div className="max-w-6xl mx-auto px-6 py-16 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
        <div className="space-y-5">
          {facilityName && (
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-slate-200 text-xs font-medium text-slate-700 shadow-sm">
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ background: primary }}
                aria-hidden="true"
              />
              {facilityName}
            </div>
          )}
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight"
            style={{ color: '#0f172a' }}
          >
            {tagline}
          </h1>
          <p className="text-base sm:text-lg text-slate-600 max-w-xl">
            {subtext}
          </p>
          <div className="flex flex-wrap gap-3 pt-2">
            <button
              type="button"
              onClick={onCta}
              className="px-5 py-2.5 rounded-md font-medium text-white shadow-sm hover:opacity-90 transition"
              style={{ background: primary }}
            >
              {ctaLabel}
            </button>
            {secondaryLabel && (
              <button
                type="button"
                onClick={onSecondary}
                className="px-5 py-2.5 rounded-md font-medium border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition"
              >
                {secondaryLabel}
              </button>
            )}
          </div>
        </div>

        <div className="flex justify-center md:justify-end">
          <HeroIllustration primary={primary} accent={accent} />
        </div>
      </div>
    </section>
  );
}

// Inline illustration: a tidy hospital bed on the left, a medication bag with
// a pill bottle floating into a cozy "home" silhouette on the right —
// reading left-to-right as "hospital → medications → home". Brand-tinted.
function HeroIllustration({ primary, accent }) {
  return (
    <svg
      viewBox="0 0 480 320"
      width="100%"
      style={{ maxWidth: 480 }}
      role="img"
      aria-label="Medications travel from the hospital bedside to the patient's home"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Soft background blob */}
      <defs>
        <linearGradient id="m2bBg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor={primary} stopOpacity="0.10" />
          <stop offset="100%" stopColor={accent}  stopOpacity="0.18" />
        </linearGradient>
        <linearGradient id="m2bBag" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor={primary} />
          <stop offset="100%" stopColor={accent} />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="480" height="320" rx="24" fill="url(#m2bBg)" />

      {/* Hospital bed (left) */}
      <g transform="translate(40,170)">
        {/* mattress */}
        <rect x="0" y="20" width="150" height="32" rx="6" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" />
        {/* pillow */}
        <rect x="6" y="10" width="46" height="22" rx="4" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" />
        {/* head/foot board */}
        <rect x="-4" y="0" width="8" height="60" rx="2" fill={primary} />
        <rect x="146" y="14" width="8" height="46" rx="2" fill={primary} opacity="0.85" />
        {/* legs */}
        <rect x="2" y="60" width="6" height="22" fill="#94a3b8" />
        <rect x="142" y="60" width="6" height="22" fill="#94a3b8" />
        {/* IV pole */}
        <line x1="-14" y1="-30" x2="-14" y2="60" stroke="#94a3b8" strokeWidth="2" />
        <rect x="-22" y="-40" width="16" height="14" rx="2" fill={accent} />
        <line x1="-14" y1="-26" x2="0" y2="14" stroke="#94a3b8" strokeWidth="1.2" strokeDasharray="3 3" />
      </g>

      {/* Curved arrow path: bedside → home */}
      <path
        d="M 220 165 Q 280 90 360 145"
        fill="none"
        stroke={primary}
        strokeWidth="2.5"
        strokeDasharray="6 6"
        strokeLinecap="round"
      />
      {/* Medication bag in the middle of the arc */}
      <g transform="translate(280,98)">
        <rect x="-22" y="-2" width="44" height="48" rx="6" fill="url(#m2bBag)" />
        <rect x="-14" y="-12" width="28" height="14" rx="3" fill="#ffffff" stroke={primary} strokeWidth="1.5" />
        {/* Rx cross */}
        <rect x="-3" y="10" width="6" height="22" fill="#ffffff" />
        <rect x="-12" y="18" width="24" height="6" fill="#ffffff" />
      </g>

      {/* Home (right) */}
      <g transform="translate(330,150)">
        {/* roof */}
        <path d="M 0 30 L 50 -10 L 100 30 Z" fill={primary} />
        {/* body */}
        <rect x="10" y="30" width="80" height="60" fill="#ffffff" stroke="#cbd5e1" strokeWidth="1.5" />
        {/* door */}
        <rect x="40" y="56" width="20" height="34" rx="2" fill={accent} />
        {/* window */}
        <rect x="18" y="42" width="14" height="14" fill={accent} opacity="0.4" stroke={accent} strokeWidth="1" />
        {/* heart on door */}
        <path d="M 50 70 c -4 -4 -10 -1 -10 4 c 0 6 10 12 10 12 s 10 -6 10 -12 c 0 -5 -6 -8 -10 -4 z" fill="#ffffff" opacity="0.95" />
      </g>
    </svg>
  );
}
